import {useTranslation} from "react-i18next";
import SectionHeader from "../../../../util/component/SectionHeader";
import Box from "@mui/material/Box/Box";
import Grid from "@mui/material/Grid/Grid";
import AgGridCustomTable from "../../../../util/component/AgGridCustomTable";
import DialogComponent from "../../common/components/DialogComponent";
import React, {useContext, useEffect, useRef, useState} from "react";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import {useAgGridLocale} from "../../../../config/globalConfig";
import {AgGridReact} from "ag-grid-react";
import {DialogInterface, SelectElementModelBE, SelectElementModelFE} from "../../api/types/generic";
import {useActionColDef} from "../../utils";
import {ColDef, ICellRendererParams} from "ag-grid-community";
import {DEFAULT_COL_DEF, SET_SNACKBAR} from "../../constants";
import dayjs from "dayjs";
import BasicToolbar from "../../common/components/BasicToolbar";
import useAgGridCustomHook from "../../hooks/useAgGridCustomHook";
import {deskDiscountGovernanceColDef} from "./deskDiscountGovernanceColDef";
import DeskDiscountGovernanceFilters from "./DeskDiscountGovernanceFilters";
import {
    DeskDiscountGovernanceEntityRequest,
    DeskDiscountGovernanceEntityResponse,
    DeskDiscountGovernanceFormModel,
    DeskDiscountGovernanceGetData,
    FormSearchDeskDiscountGovernanceModel
} from "../../api/types/deskDiscountGovernance";
import {useDeskDiscountGovernanceRepository} from "../../api/repositories/DeskDiscountGovernanceRepository";
import {Button, Dialog, DialogContent, useTheme} from "@mui/material";
import ActionsPopover from "../../common/components/ActionsPopover";
import BootstrapDialogTitle from "../../common/components/BootstrapDialogTitle";
import {FormProvider, useForm} from "react-hook-form";
import DeskDiscountGovernanceForm from "./DeskDiscountGovernanceForm";
import DeskDiscountGovernanceEditForm from "./DeskDiscountGovernanceEditForm";
import * as yup from "yup";
import {yupResolver} from "@hookform/resolvers/yup/dist/yup";
import CustomLoader from "../../../../util/component/CustomLoader";

const DeskDiscountGovernance = () => {

    // Use Theme
    const theme = useTheme();

    // Use Context
    const {dispatchGlobalData, globalData: {user}} = useContext(GlobalContext);

    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    // Use AgGridLocale
    const agGridLocale = useAgGridLocale();

    // Use Ref
    const gridRef = useRef<AgGridReact>(null);

    // Use Repository Hooks
    const {
        getDeskDiscountGovernancePaged,
        getDeskDiscountGovernanceDatas,
        postDeskDiscountGovernance,
        getActiveGovernanceForScope,
        deleteDeskDiscountGovernance,
        downloadError
    } = useDeskDiscountGovernanceRepository();

    // Use State
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [dialog, setDialog] = useState<DialogInterface | null>(null);
    const [openDialog, setOpenDialog] = useState<boolean>(false);
    const [responseData, setResponseData] = useState<DeskDiscountGovernanceGetData>();
    const [responseDataOrigin, setResponseDataOrigin] = useState<DeskDiscountGovernanceGetData>();
    // Create massiva: report righe scartate (byte/xlsx) restituito dal BE
    const [downloadResponse, setDownloadResponse] = useState<any>(null);
    const [receivedResponse, setReceivedResponse] = useState<boolean>(false);
    const [hideFirstButton, setHideFirstButton] = useState<boolean>(false);
    // Edit singolo record (toggle active + range %)
    const [isEdit, setIsEdit] = useState<boolean>(false);
    const [selectedRow, setSelectedRow] = useState<DeskDiscountGovernanceEntityResponse | null>(null);

    const [initialValues] = useState<DeskDiscountGovernanceFormModel>({
        prcLocation: [],
        lppProfiles: [],
        discountMin: "",
        discountMax: "",
        active: true,
        validityDateFrom: "",
        validityDateTo: "",
        dealer: null,
        legalEntity: null
    });

    // Legal entity options
    const legalEntityOptions: SelectElementModelFE[] = user.legalEntities?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:legalEntities.'.concat(el.name))
        })
    );

    const everyLegalEntitiesWithoutAll = legalEntityOptions
        .map((el) => el.code as string)
        .filter((el) => el !== "all");

    const dealerOption: SelectElementModelFE[] = user.dealers?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:dealers.'.concat(el.name))
        })
    );

    const dealerFilteredOptions = dealerOption
        .map((el) => el.code as string)
        .filter((el) => el !== "all");

    // Fetch dropdown data
    const getResponseData = async () => {
        const data: DeskDiscountGovernanceGetData = await getDeskDiscountGovernanceDatas(everyLegalEntitiesWithoutAll, dealerFilteredOptions);
        setResponseDataOrigin(data);
        setResponseData(data);
    };

    useEffect(() => {
        getResponseData();
    }, []);

    // Filters
    const [testFilters, setTestFilters] = useState<FormSearchDeskDiscountGovernanceModel>({
        id: "",
        locationCodes: [],
        validityFrom: "",
        validityTo: "",
        lppProfileIds: [],
        active: null,
        dealers: dealerFilteredOptions,
        legalEntities: everyLegalEntitiesWithoutAll
    });

    useEffect(() => {
        if (responseDataOrigin) {
            const selectedLegalEntities = Array.isArray(testFilters.legalEntities)
                ? testFilters.legalEntities.map((el: any) => el.code as string)
                : typeof testFilters.legalEntities === "string"
                    ? [testFilters.legalEntities]
                    : (testFilters.legalEntities as SelectElementModelFE)?.code
                        ? [(testFilters.legalEntities as SelectElementModelFE).code as string]
                        : [];

            const location = responseDataOrigin?.locations.filter(loc =>
                selectedLegalEntities.includes(loc.legalEntity)
            );

            setResponseData({...responseDataOrigin, locations: location});
        }
    }, [testFilters.legalEntities]);

    // Use Grid Data Custom Hook
    const {setServerSideDatasource, handleTriggerFilters, refresh} =
        useAgGridCustomHook<
            DeskDiscountGovernanceEntityResponse,
            FormSearchDeskDiscountGovernanceModel
        >({
            gridRef,
            api: getDeskDiscountGovernancePaged,
            initialSortColId: "validityFrom",
            entityFilters: testFilters,
            customSort: "DeskDiscountGovernanceEntityResponse",
        });

    useEffect(() => {
        handleTriggerResetFilters();
    }, []);

    const handleTriggerResetFilters = () => {
        setTestFilters({
            id: "",
            locationCodes: [],
            validityFrom: "",
            validityTo: "",
            lppProfileIds: [],
            active: null,
            dealers: [],
            legalEntities: []
        });
        handleTriggerFilters({
            id: "",
            locationCodes: [],
            validityFrom: "",
            validityTo: "",
            lppProfileIds: [],
            active: null,
            dealers: [],
            legalEntities: []
        });
    };

    const handleGetList = () => {
        handleTriggerFilters({
            id: testFilters.id ?? "",
            locationCodes: (testFilters.locationCodes as SelectElementModelFE[]).map(el => el.code as string),
            validityFrom: testFilters.validityFrom
                ? dayjs(testFilters.validityFrom).toISOString()
                : testFilters.validityFrom,
            validityTo: testFilters.validityTo
                ? dayjs(testFilters.validityTo).toISOString()
                : testFilters.validityTo,
            lppProfileIds: (testFilters.lppProfileIds as SelectElementModelFE[]).map(el => el.code as number),
            // il select "attivo" tiene l'oggetto {code,label}: invio solo lo scalare ("true"/"false") o null
            active: (testFilters.active as SelectElementModelFE | null)?.code?.toString() ?? null,
            legalEntities:
                ((testFilters.legalEntities as SelectElementModelFE)?.code as string) === "all"
                    ? everyLegalEntitiesWithoutAll
                    : (testFilters.legalEntities as SelectElementModelFE[]).map(el => el.code as string),
            dealers:
                ((testFilters.dealers as SelectElementModelFE)?.code as string) === "all"
                    ? dealerFilteredOptions
                    : (testFilters.dealers as SelectElementModelFE[]).map(el => el.code as string),
        });
    };

    // Validation Schema (yup)
    const validationSchema = yup.object().shape({
        validityDateFrom: yup
            .string()
            .nullable()
            .required(t("translation:validation.fieldMandatory")),
        validityDateTo: yup
            .string()
            .nullable()
            .test(
                "to-after-from",
                t("pricing:deskDiscountGovernance.validation.toAfterFrom"),
                function (value) {
                    const {validityDateFrom} = this.parent;
                    // vuoto = validità aperta (ammesso); altrimenti deve essere > from
                    // (evita periodi a durata zero non gestiti dall'overlap lato Pricing)
                    if (!value || !validityDateFrom) return true;
                    return dayjs(value).isAfter(dayjs(validityDateFrom));
                }
            ),
        discountMin: yup
            .number()
            .typeError(t("translation:validation.fieldMandatory"))
            .required(t("translation:validation.fieldMandatory"))
            .min(0, t("pricing:deskDiscountGovernance.validation.minRange"))
            .max(100, t("pricing:deskDiscountGovernance.validation.maxRange"))
            .test(
                "multiple-of-5",
                t("pricing:deskDiscountGovernance.validation.multipleOfFive"),
                (value) => value == null || Number(value) % 5 === 0
            ),
        discountMax: yup
            .number()
            .typeError(t("translation:validation.fieldMandatory"))
            .required(t("translation:validation.fieldMandatory"))
            .min(0, t("pricing:deskDiscountGovernance.validation.minRange"))
            .max(100, t("pricing:deskDiscountGovernance.validation.maxRange"))
            .test(
                "multiple-of-5",
                t("pricing:deskDiscountGovernance.validation.multipleOfFive"),
                (value) => value == null || Number(value) % 5 === 0
            )
            .test(
                "max-gte-min",
                t("pricing:deskDiscountGovernance.validation.maxGteMin"),
                function (value) {
                    const {discountMin} = this.parent;
                    if (value == null || discountMin == null) return true;
                    return value >= discountMin;
                }
            ),
        prcLocation: yup.array().of(
            yup.object().shape({
                code: yup.string(),
                label: yup.string()
            }))
            .min(1, t("pricing:deskDiscountGovernance.validation.atLeastOneLocation"))
            .nullable()
            .required(t("pricing:deskDiscountGovernance.validation.atLeastOneLocation")),
        lppProfiles: yup.array().of(
            yup.object().shape({
                code: yup.string(),
                label: yup.string()
            }))
            .min(1, t("pricing:deskDiscountGovernance.validation.atLeastOneProfile"))
            .nullable()
            .required(t("pricing:deskDiscountGovernance.validation.atLeastOneProfile")),
        legalEntity: yup
            .object()
            .shape({
                code: yup.string(),
                label: yup.string()
            })
            .nullable()
            .required(t("translation:validation.fieldMandatory")),
    }).defined();

    // Use Form
    const form = useForm<DeskDiscountGovernanceFormModel>({
        defaultValues: initialValues,
        resolver: yupResolver(
            validationSchema,
            {},
            {rawValues: true}
        ),
        shouldUnregister: true,
    });

    // Actions Col Def
    const {actionColDef} = useActionColDef();

    const columnDefs: ColDef<DeskDiscountGovernanceEntityResponse>[] = deskDiscountGovernanceColDef({
        actionsRenderer: (params: ICellRendererParams) => (
            <ActionsPopover
                params={params}
                update={{
                    action: editButtonClicked,
                    userHasAuth: !!user.profiles.find((profile: any) =>
                        profile.auths.find((auth: any) => auth.name === "SCOPE_PRC_UPDATE_DESK_DISCOUNT_GOVERNANCE")),
                }}
                del={{
                    action: deleteButtonClicked,
                    userHasAuth: !!user.profiles.find((profile: any) =>
                        profile.auths.find((auth: any) => auth.name === "SCOPE_PRC_DELETE_DESK_DISCOUNT_GOVERNANCE")),
                }}
            />
        ),
        actionColDef,
        user
    });

    const toolbar = {
        renderActions: () => (
            <BasicToolbar
                addIcon={{
                    onClick: () => {
                        setIsEdit(false);
                        setSelectedRow(null);
                        form.reset(initialValues);
                        setOpenDialog(true);
                    }
                }}
            />
        ),
    };

    // Delete handler
    const handleConfirmDelete = async (id: number) => {
        try {
            if (id) {
                await deleteDeskDiscountGovernance(id);
                setDialog(null);
                refresh();
                dispatchGlobalData(SET_SNACKBAR.SUCCESS);
            }
        } catch (e) {
            // Error handled by useApi
        }
    };

    const deleteButtonClicked = (data: DeskDiscountGovernanceEntityResponse) => {
        setDialog({
            open: true,
            onClickConfirm: () => handleConfirmDelete(data.id),
            message: t("pricing:deskDiscountGovernance.deleteConfirm"),
        });
    };

    // Edit handler: apre il dialog in modalita' modifica (solo toggle active, via lista scope)
    const editButtonClicked = (data: DeskDiscountGovernanceEntityResponse) => {
        setSelectedRow(data);
        setIsEdit(true);
        setOpenDialog(true);
    };

    // Callback dopo un toggle active nella lista scope: riallinea la griglia e notifica successo
    const handleEditChanged = () => {
        refresh();
        dispatchGlobalData(SET_SNACKBAR.SUCCESS);
    };

    // Verifica overlap tra due periodi di validità; validityDateTo nullo = validità aperta (+∞).
    const periodsOverlap = (aFrom: string | null, aTo: string | null, bFrom: string | null, bTo: string | null): boolean => {
        if (!aFrom || !bFrom) return false;
        const aStart = dayjs(aFrom);
        const aEnd = aTo ? dayjs(aTo) : null;
        const bStart = dayjs(bFrom);
        const bEnd = bTo ? dayjs(bTo) : null;
        const cond1 = bEnd === null || !aStart.isAfter(bEnd); // aFrom <= bTo (o bTo aperto)
        const cond2 = aEnd === null || !bStart.isAfter(aEnd); // bFrom <= aTo (o aTo aperto)
        return cond1 && cond2;
    };

    // Esegue l'inserimento vero e proprio (post) e gestisce l'esito.
    const doInsert = async (request: DeskDiscountGovernanceEntityRequest) => {
        await setIsLoading(true);
        try {
            const response = await postDeskDiscountGovernance(request);
            const hasDiscardedRows = response?.data?.byteLength != null && response.data.byteLength !== 0;
            if (hasDiscardedRows) {
                refresh();
                setDownloadResponse(response);
                setReceivedResponse(true);
                setHideFirstButton(true);
            } else {
                handleConfirmCloseDialog();
            }
        } catch (e) {
            // Error handled by useApi
        }
        setIsLoading(false);
    };

    // Submit handler
    const handleConfirmSave = async () => {
        const watchAllForm = form.getValues();

        const request: DeskDiscountGovernanceEntityRequest = {
            locationCodes: watchAllForm.prcLocation.map(el => el.code) as string[],
            idProfiles: watchAllForm.lppProfiles.map(el => ({
                id: el.code as number,
                label: el.label as string
            })),
            // % inserita dall'utente → centesimi di punto (es. 10 → 1000)
            discountMin: Math.round(Number(watchAllForm.discountMin) * 100),
            discountMax: Math.round(Number(watchAllForm.discountMax) * 100),
            active: watchAllForm.active,
            validityDateFrom: watchAllForm.validityDateFrom ? dayjs(watchAllForm.validityDateFrom).toISOString() : "",
            validityDateTo: watchAllForm.validityDateTo ? dayjs(watchAllForm.validityDateTo).toISOString() : null,
            dealer: dealerFilteredOptions.length === 1 ? dealerFilteredOptions[0] : (watchAllForm.dealer as SelectElementModelFE)?.code as string,
            legalEntity: legalEntityOptions.length === 1 ? everyLegalEntitiesWithoutAll[0] : (watchAllForm.legalEntity as SelectElementModelFE)?.code as string,
        };

        const prcLocation = await form.trigger("prcLocation");
        const validityDateFrom = await form.trigger("validityDateFrom");
        const validityDateTo = await form.trigger("validityDateTo");
        const discountMin = await form.trigger("discountMin");
        const discountMax = await form.trigger("discountMax");
        const lppProfiles = await form.trigger("lppProfiles");

        if (!(prcLocation && validityDateFrom && validityDateTo && discountMin && discountMax && lppProfiles)) {
            return;
        }

        // Se la nuova configurazione nasce ATTIVA, verifica se esistono configurazioni attive
        // sovrapposte sullo stesso scope: in tal caso avvisa l'operatore che verranno disattivate
        // (la disattivazione effettiva è garantita dal backend alla creazione).
        if (request.active) {
            try {
                const resp = await getActiveGovernanceForScope({
                    locationCodes: request.locationCodes,
                    lppProfileIds: request.idProfiles.map(p => p.id),
                    dealers: [request.dealer],
                    legalEntities: [request.legalEntity],
                });
                const conflicts = (resp?.content ?? []).filter(c =>
                    c.active && periodsOverlap(request.validityDateFrom, request.validityDateTo, c.validityDateFrom, c.validityDateTo)
                );
                if (conflicts.length > 0) {
                    setDialog({
                        open: true,
                        onClickConfirm: () => {
                            setDialog(null);
                            doInsert(request);
                        },
                        message: t("pricing:deskDiscountGovernance.parallelActiveConfirm", {count: conflicts.length}),
                    });
                    return;
                }
            } catch (e) {
                // Se il check fallisce, procedi comunque: il backend gestisce la disattivazione.
            }
        }

        doInsert(request);
    };

    // Chiusura dialog senza download (nessuna riga scartata)
    const handleConfirmCloseDialog = async () => {
        setReceivedResponse(false);
        setHideFirstButton(false);
        setOpenDialog(false);
        refresh();
        dispatchGlobalData(SET_SNACKBAR.SUCCESS);
    };

    // Download del report xlsx delle righe scartate
    const handleConfirmDownload = async () => {
        await setIsLoading(true);
        try {
            await downloadError(downloadResponse);
        } catch (e) {
            // ignore
        }
        setReceivedResponse(false);
        setHideFirstButton(false);
        setOpenDialog(false);
        refresh();
        dispatchGlobalData(SET_SNACKBAR.SUCCESS);
        setIsLoading(false);
    };

    const closeDialog = () => {
        setReceivedResponse(false);
        setHideFirstButton(false);
        setIsEdit(false);
        setSelectedRow(null);
        setOpenDialog(false);
        // Le righe eventualmente create restano persistite: riallinea la griglia anche su chiusura via X.
        refresh();
    };

    return (
        <Box>
            <SectionHeader
                title={t("pricing:deskDiscountGovernance.title")}
            />

            <DeskDiscountGovernanceFilters
                handleGetList={handleGetList}
                handleClickResetSearch={handleTriggerResetFilters}
                testFilters={testFilters}
                setTestFilters={setTestFilters}
                responseData={responseData}
            />

            <Grid container spacing={2} p={2}>
                <Grid item xs={12} sm={12}>
                    <AgGridCustomTable
                        gridRef={gridRef}
                        toolbar={toolbar}
                        defaultColDef={DEFAULT_COL_DEF}
                        columnDefs={columnDefs}
                        pagination
                        localeText={agGridLocale}
                        tooltipShowDelay={0}
                        domLayout="autoHeight"
                        rowModelType="serverSide"
                        paginationPageSize={10}
                        cacheBlockSize={10}
                        serverSideInfiniteScroll
                        onGridReady={setServerSideDatasource}
                    />
                    {dialog?.open && (
                        <DialogComponent
                            type={"info"}
                            onClickConfirm={dialog.onClickConfirm}
                            setOpen={() => setDialog(null)}
                            open={dialog.open}
                            message={dialog.message}
                        />
                    )}
                </Grid>
            </Grid>

            <Dialog open={openDialog} maxWidth={"xl"} fullWidth>
                <BootstrapDialogTitle id="customized-dialog-title" end={'end'}
                                      onClose={closeDialog}></BootstrapDialogTitle>
                <DialogContent style={{color: theme.palette.primary.main}}>
                    {isEdit && selectedRow ? (
                        <DeskDiscountGovernanceEditForm row={selectedRow} onChange={handleEditChanged}/>
                    ) : (
                        <FormProvider {...form}>
                            <DeskDiscountGovernanceForm/>
                        </FormProvider>
                    )}
                    {!isEdit && hideFirstButton && receivedResponse && (
                        <div style={{marginTop: "1em", color: theme.palette.warning.main}}>
                            {t("pricing:deskDiscountGovernance.partialKo")}
                        </div>
                    )}
                    <div style={{display: "flex", justifyContent: "end", alignItems: "end"}}>
                        {isEdit ? (
                            <Button style={{marginLeft: "15px"}} size="small"
                                    onClick={closeDialog}>{t("pricing:actions.close")}</Button>
                        ) : (
                            <>
                                {!hideFirstButton && (
                                    <Button type="submit" style={{marginLeft: "15px"}} size="small"
                                            onClick={handleConfirmSave}>{t("pricing:actions.confirm")}</Button>
                                )}
                                {hideFirstButton && receivedResponse && (
                                    <Button style={{marginLeft: "15px"}} size="small"
                                            onClick={handleConfirmDownload}>{t("pricing:actions.download")}</Button>
                                )}
                                {hideFirstButton && !receivedResponse && (
                                    <Button style={{marginLeft: "15px"}} size="small"
                                            onClick={handleConfirmCloseDialog}>{t("pricing:actions.close")}</Button>
                                )}
                            </>
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            <CustomLoader loading={isLoading}/>
        </Box>
    );
};

export default DeskDiscountGovernance;
